/* eslint-disable no-unused-vars */
import Vue from 'vue';
import VueRouter from 'vue-router';
import routes from './routes.js';
import store from '../store/vuex';
import { sleepAsync } from '@/lib/Utils';

Vue.use(VueRouter);

function hasToken() {
  return Boolean(localStorage.getItem('wims_token'));
}

const router = new VueRouter({
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return { selector: to.hash, behavior: 'smooth' };
    }

    if (to.path === from.path) {
      const pageChanged = to.query?.page !== from.query?.page;
      const perPageChanged = to.query?.itemsPerPage !== from.query?.itemsPerPage;

      // Keep viewport stable for same-page query sync (filters/search),
      // but scroll to top for pagination changes.
      if (!pageChanged && !perPageChanged) {
        return false;
      }
    }

    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const { query, meta, path, fullPath } = to;
  const logged = hasToken();
  const redirectPath = query?.r || null;
  const isPublicRedirect = /^\/(reset-password\/[a-z0-9]{40,}|prices|\d+)$/i.test(redirectPath || '');

  if (isPublicRedirect && !logged && redirectPath) {
    const query = { ...to.query };
    delete query.r;
    next({
      path: redirectPath,
      query,
    });
    return;
  }
  if (!logged && !meta.isPublic) {
    next({ path: '/', query: { ...to.query, r: fullPath } });
    return;
  }
  if (!logged && meta.isPublic) {
    next();
    return;
  }
  if (logged && redirectPath) {
    const query = { ...to.query };
    delete query.r;
    next({
      path: redirectPath,
      query,
    });
    return;
  }

  while (!store.state.flags.loaded) {
    await sleepAsync(10);
  }
  const { isPublic, restrictions, restriction: role, redirectPage } = to.meta;

  const roles = role ? (Array.isArray(role) ? role : [role]) : restrictions || [];
  const restricted = isRestricted(roles) || isParamRestricted(to, restrictions);
  if (restricted && !isPublic) {
    if (redirectPage) {
      next({ path: typeof redirectPage === 'function' ? redirectPage() : redirectPage });
    } else {
      next('/403');
    }
    return;
  }
  next();
});

export default router;

function isParamRestricted(route, restrictions) {
  if (!restrictions) {
    return false;
  }
  const { param, map } = restrictions;
  if (!param || !map) {
    return false;
  }
  const { [param]: key } = route.params;
  if (!key) {
    return false;
  }

  if (!map[key]) {
    return true;
  }
  const roles = Array.isArray(map[key]) ? map[key] : [map[key]];
  return !roles.some((role) => {
    if (Array.isArray(role)) {
      return role.every((item) => store.state.user.hasAccess(item));
    }
    return store.state.user.hasAccess(role);
  });
}

function isRestricted(roles) {
  if (!roles?.length) {
    return false;
  }
  return !roles.some((role) => {
    if (Array.isArray(role)) {
      return role.every((item) => store.state.user.hasAccess(item));
    }
    return store.state.user.hasAccess(role);
  });
}
